import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Spin, 
  Typography, 
  Select, 
  Alert,
  Space,
  App,
  Result 
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  TeamOutlined 
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import businessUnitService from '../services/businessUnitService';
import PhoneNumberInput from './PhoneNumberInput';

const { Title, Text } = Typography;
const { Option } = Select;

const ParticipantRegister = () => {
  const { message: messageApi } = App.useApp(); // Use the App context for messages
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState('+27');
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredUserData, setRegisteredUserData] = useState(null);
  const { token } = useParams();
  const navigate = useNavigate();

  // Helper function to show messages with fallback
  const showMessage = (text, type = 'info') => {
    try {
      console.log(`📢 Showing ${type} message:`, text);
      switch (type) {
        case 'success':
          messageApi.success(text);
          break;
        case 'error':
          messageApi.error(text);
          break;
        case 'warning':
          messageApi.warning(text);
          break;
        default:
          messageApi.info(text);
      }
    } catch (messageError) {
      console.error('Message API failed, trying static message:', messageError);
      try {
        // Fallback to static message
        switch (type) {
          case 'success':
            messageApi.success(text);
            break;
          case 'error':
            messageApi.error(text);
            break;
          case 'warning':
            messageApi.warning(text);
            break;
          default:
            messageApi.info(text);
        }
      } catch (staticError) {
        console.error('Static message also failed:', staticError);
        // Ultimate fallback to alert
        alert(`${type.toUpperCase()}: ${text}`);
      }
    }
  };

  useEffect(() => {
    validateInvitation();
  }, [token]);

  // Monitor form errors to disable submit button
  useEffect(() => {
    const fieldsError = form.getFieldsError();
    const hasErrors = fieldsError.some(({ errors }) => errors.length > 0);
    setHasFormErrors(hasErrors);
  }, [form]);

  const validateInvitation = async () => {
    if (!token) {
      setValidating(false);
      showMessage('Invalid invitation link', 'error');
      navigate('/');
      return;
    }

    try {
      setValidating(true);
      const result = await userService.getParticipantInvitationDetails(token);
      
      if (result.success) {
        setInvitationData(result.data.data);
        showMessage('Invitation validated successfully!', 'success');
      } else {
        showMessage(result.message || 'Invalid invitation link', 'error');
        navigate('/');
      }
    } catch (error) {
      showMessage('Failed to validate invitation', 'error');
      navigate('/');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Prepare phone number object if provided
      const phoneNumberData = values.phoneNumber ? {
        countryCode: selectedCountryCode,
        number: values.phoneNumber,
        full: `${selectedCountryCode}${values.phoneNumber}`
      } : null;
      
      const result = await userService.completeParticipantRegistration(token, {
        name: values.name,
        surname: values.surname,
        email: values.email,
        phoneNumber: phoneNumberData,
        department: values.department
      });

      console.log('Registration result:', result);

      if (result.success) {
        console.log('✅ Registration successful, showing success page');
        
        // Store registration data for success page
        setRegisteredUserData({
          name: values.name,
          surname: values.surname,
          email: values.email,
          department: values.department,
          businessUnit: invitationData?.businessUnit?.name,
          phoneNumber: values.phoneNumber ? `${selectedCountryCode}${values.phoneNumber}` : null
        });
        
        // Show success page instead of navigating to login
        setRegistrationComplete(true);
      } else {
        console.log('❌ Registration failed:', result.message);
        
        // Show generic error message for any registration failure
        if (result.message && (result.message.includes('email') || result.message.includes('phone') || result.message.includes('duplicate') || result.message.includes('exists'))) {
          showMessage('Email or phone number already used. Please contact your administrator.', 'error');
        } else {
          showMessage('Registration failed. Please contact your administrator.', 'error');
        }
      }
    } catch (error) {
      console.log('🚨 Registration error:', error);
      showMessage('Registration failed. Please contact your administrator.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!invitationData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Card style={{ maxWidth: 400 }}>
          <Alert
            message="Invalid Invitation"
            description="This invitation link is invalid or has expired."
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  const businessUnit = invitationData?.businessUnit;

  // Show success page after registration
  if (registrationComplete && registeredUserData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: window.innerWidth <= 768 ? '10px' : '20px'
      }}>
        <Card style={{ 
          maxWidth: 600, 
          width: '100%',
          padding: window.innerWidth <= 768 ? '15px' : '20px',
          margin: window.innerWidth <= 768 ? '0' : 'auto'
        }}>
          <Result
            status="success"
            title="Welcome to TapLearnGo!"
            subTitle={
              <div style={{ textAlign: 'left', padding: '20px 0' }}>
                <Text strong style={{ fontSize: '16px', color: '#2E86AB' }}>
                  Thank you for joining us, {registeredUserData.name}!
                </Text>
                <br /><br />
                <Text style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  <strong>Registration Summary:</strong><br />
                  • <strong>Name:</strong> {registeredUserData.name} {registeredUserData.surname}<br />
                  • <strong>Email:</strong> {registeredUserData.email}<br />
                  {registeredUserData.phoneNumber && (
                    <>• <strong>Phone:</strong> {registeredUserData.phoneNumber}<br /></>
                  )}
                  • <strong>Business Unit:</strong> {registeredUserData.businessUnit}<br />
                  • <strong>Department:</strong> {registeredUserData.department}
                </Text>
                <br /><br />
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Your account has been successfully created. 
                </Text>
              </div>
            }
            extra={[
              
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: window.innerWidth <= 768 ? '10px' : '20px'
    }}>
      <Card style={{ 
        maxWidth: 500, 
        width: '100%',
        padding: window.innerWidth <= 768 ? '15px' : '20px',
        margin: window.innerWidth <= 768 ? '0' : 'auto'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
           <img 
            src="/logo.png" 
            alt="TapLearnGo Logo"
            className="w-3/4 h-auto mx-auto"
            style={{ backgroundColor: 'transparent', marginTop: '-20px' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}></img>
          <Title level={2}>Complete Your Registration</Title>
          <Text type="secondary">
            You've been invited to join <strong>{businessUnit?.name}</strong>
          </Text>
         
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={loading}
        >
          <Form.Item
            name="name"
            label="First Name"
            rules={[
              { required: true, message: 'Please enter your first name' },
              { min: 2, message: 'Name must be at least 2 characters' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter your first name"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="surname"
            label="Last Name"
            rules={[
              { required: true, message: 'Please enter your last name' },
              { min: 2, message: 'Surname must be at least 2 characters' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter your last name"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input 
              prefix={<MailOutlined />} 
              placeholder="Enter your email address"
              size="large"
            />
          </Form.Item>

          <PhoneNumberInput
            form={form}
            businessUnitId={invitationData?.businessUnit?._id}
            initialPhone=""
            initialCountryCode={selectedCountryCode}
            onCountryCodeChange={setSelectedCountryCode}
            onPhoneValidation={(isValid, phone, countryCode) => {
              if (isValid) {
                form.setFieldsValue({ phoneNumber: phone });
              }
            }}
          />

          <Form.Item
            name="department"
            label="Department"
            rules={[
              { required: true, message: 'Please select your department' }
            ]}
          >
            <Select
              prefix={<TeamOutlined />}
              placeholder="Select your department"
              size="large"
            >
              {businessUnit?.departments?.map(dept => (
                <Option key={dept} value={dept}>
                  {dept}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginTop: '30px' }}>
            <Button
              htmlType="submit"
              size="large"
              block
              loading={loading}
              disabled={hasFormErrors}
              style={{ 
                background: '#2E86AB',
                borderColor: '#2E86AB',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Complete Registration
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            By registering, you agree to the terms of service and privacy policy.
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default ParticipantRegister;